/**
 * The language the setup agent speaks and writes, chosen before the call.
 *
 * Eleven, in the order the picker draws them. The pick rides in the chat
 * context, so the interviewer writes in it; goes to the transcriber as a
 * hint, so a Hindi answer is heard as Hindi; and steers the one voice, so the
 * greeting sounds like the language it is in. The app's own labels stay in
 * English: this is the agent's language, not the phone's.
 *
 * Data, not keys, as `currencies.ts` is: a language's own name is a proper
 * noun and not our word. The four sentences the phone speaks without a model
 * call (`setup.call.greeting` and its three siblings) are the one exception,
 * and `spokenKey` says where each language's version sits in the catalogue.
 * Board: docs/design/agent-language.
 */
import type { AgentVoice } from './auth';

export const AGENT_LANGUAGE_TAGS = ['en', 'hi', 'hi-Latn', 'mr', 'gu', 'bn', 'ta', 'te', 'kn', 'ml', 'pa'] as const;

/** BCP 47. `hi-Latn` is Hinglish: Hindi words in Latin letters, mixed with English, as people text. */
export type AgentLanguageTag = (typeof AGENT_LANGUAGE_TAGS)[number];

export type AgentLanguage = {
  readonly tag: AgentLanguageTag;
  /** The ISO 639-1 code the transcriber is given. Hinglish is heard as Hindi. */
  readonly stt: string;
  /** The name in the language itself: the row on the picker. */
  readonly native: string;
  /** The name in English: the line under the row, and the word the prompt uses. */
  readonly english: string;
  /** How the interviewer is told to write. One clause, read into the prompt. */
  readonly writes: string;
  /** How the voice is told to sound. One clause, read into the synthesis instructions. */
  readonly sounds: string;
};

export const AGENT_LANGUAGES: readonly AgentLanguage[] = [
  { tag: 'en', stt: 'en', native: 'English', english: 'English', writes: 'English', sounds: 'English with a natural Indian accent' },
  { tag: 'hi', stt: 'hi', native: 'हिन्दी', english: 'Hindi', writes: 'Hindi, in Devanagari', sounds: 'Hindi' },
  {
    tag: 'hi-Latn',
    stt: 'hi',
    native: 'Hinglish',
    english: 'Hindi and English, mixed',
    writes: 'Hinglish: Hindi words written in Latin letters, mixed with English for the trade words, the way people text',
    sounds: 'Hinglish, reading Latin-letter Hindi words as Hindi and the English words as an Indian speaker says them',
  },
  { tag: 'mr', stt: 'mr', native: 'मराठी', english: 'Marathi', writes: 'Marathi, in Devanagari', sounds: 'Marathi' },
  { tag: 'gu', stt: 'gu', native: 'ગુજરાતી', english: 'Gujarati', writes: 'Gujarati, in Gujarati script', sounds: 'Gujarati' },
  { tag: 'bn', stt: 'bn', native: 'বাংলা', english: 'Bengali', writes: 'Bengali, in Bengali script', sounds: 'Bengali' },
  { tag: 'ta', stt: 'ta', native: 'தமிழ்', english: 'Tamil', writes: 'Tamil, in Tamil script', sounds: 'Tamil' },
  { tag: 'te', stt: 'te', native: 'తెలుగు', english: 'Telugu', writes: 'Telugu, in Telugu script', sounds: 'Telugu' },
  { tag: 'kn', stt: 'kn', native: 'ಕನ್ನಡ', english: 'Kannada', writes: 'Kannada, in Kannada script', sounds: 'Kannada' },
  { tag: 'ml', stt: 'ml', native: 'മലയാളം', english: 'Malayalam', writes: 'Malayalam, in Malayalam script', sounds: 'Malayalam' },
  { tag: 'pa', stt: 'pa', native: 'ਪੰਜਾਬੀ', english: 'Punjabi', writes: 'Punjabi, in Gurmukhi', sounds: 'Punjabi' },
];

export const DEFAULT_AGENT_LANGUAGE: AgentLanguageTag = 'en';

export const isAgentLanguageTag = (value: unknown): value is AgentLanguageTag =>
  typeof value === 'string' && (AGENT_LANGUAGE_TAGS as readonly string[]).includes(value);

export const agentLanguageOf = (tag: AgentLanguageTag): AgentLanguage =>
  AGENT_LANGUAGES.find((language) => language.tag === tag) ?? AGENT_LANGUAGES[0]!;

/**
 * The language a device locale points at, or English. `hi-IN` is Hindi;
 * `en-IN` is English; a locale in none of the eleven is English, and nobody's
 * row is marked as the phone's. The script subtag is not read: a phone set to
 * Hindi is offered Hindi, and Hinglish is a choice a person makes on purpose.
 */
export const agentLanguageForLocale = (locale: string | null | undefined): AgentLanguageTag => {
  if (locale === null || locale === undefined) return DEFAULT_AGENT_LANGUAGE;
  const primary = locale.trim().toLowerCase().split(/[-_]/)[0] ?? '';
  const match = AGENT_LANGUAGE_TAGS.find((tag) => tag === primary);
  return match ?? DEFAULT_AGENT_LANGUAGE;
};

/** The sentences the phone speaks on its own, without a model call. */
export type SpokenMoment = 'greeting' | 'are_you_there' | 'no_words' | 'here_it_is' | 'ready';

/**
 * The catalogue key for one of those sentences in one language. English is
 * the key that shipped; every other language sits under it by tag.
 */
export const spokenKey = (tag: AgentLanguageTag, moment: SpokenMoment): string =>
  tag === 'en' ? `setup.call.${moment}` : `setup.call.${moment}.${tag}`;

/**
 * Live mode's fixed spoken lines: the server's own words, said without a
 * model's choosing (os-voice-conversation.md, Appendix C). The `away`,
 * `card_waiting`, `setup_ready_web` and `setup_ready_entry` lines are the web's;
 * `setup_ready_entry` names the trade's entry (`{{name}}`, `{{entry}}`).
 * `studio_hold` and `studio_still` are the studio seat's while a line's draft
 * runs (RF7, RF9 of docs/plans/receipts-first-words.md): the hold line said
 * in place of words that stated a change before it landed, and the one
 * progress line of a long draft. Neither names a part or an outcome.
 */
export const LIVE_SPOKEN_MOMENTS = [
  'idle_check',
  'goodbye',
  'quiet',
  'bare_yes',
  'create_yes',
  'tap_only',
  'parked',
  'rollover',
  'quota',
  'setup_ready',
  'create_tap',
  'setup_built',
  'away',
  'card_waiting',
  'setup_ready_web',
  'setup_ready_entry',
  'say_name',
  'create_click',
  'half_why',
  'studio_hold',
  'studio_still',
] as const;
export type LiveSpokenMoment = (typeof LIVE_SPOKEN_MOMENTS)[number];

/**
 * The catalogue keys that may hold one of live mode's spoken lines, in the
 * order to try them: the chosen voice's own form (for a line whose first
 * person is gendered), the form every voice shares, then English. English is
 * the bare key; every other language sits under it by tag, as `spokenKey`
 * does. Only hi and hi-Latn have lines of their own today; the others fall
 * back to English.
 */
export const liveSpokenKeys = (tag: AgentLanguageTag, moment: LiveSpokenMoment, voice: AgentVoice): readonly string[] => {
  const stem = `agent.live.say.${moment}`;
  if (tag === 'en') return [`${stem}_${voice}`, stem];
  return [`${stem}.${tag}_${voice}`, `${stem}.${tag}`, stem];
};

/** The first of `liveSpokenKeys` the catalogue holds; English when none does. */
export const liveSpokenKeyIn = (catalogue: Readonly<Record<string, string>>, tag: AgentLanguageTag, moment: LiveSpokenMoment, voice: AgentVoice): string =>
  liveSpokenKeys(tag, moment, voice).find((key) => catalogue[key] !== undefined) ?? `agent.live.say.${moment}`;

/* ------------------------------------------------ Coach's voice clips */

/**
 * The version of every voice clip's words. Bump it whenever a clip's words
 * change, so the phone's cache names a new file and never plays the old one.
 */
export const AGENT_CLIP_VERSION = 2;

/**
 * The clips the server makes once in each voice and serves from one route
 * (S15): the voice sample Account plays (Phase 14), and the fillers a live
 * call plays while Coach works.
 */
export type AgentClipKind = 'preview' | 'filler';

/** How many fillers there are, played in order while Coach works. */
export const FILLER_COUNT = 3;

/**
 * The catalogue keys that may hold a clip's words, in the order to try them:
 * the chosen voice's own form first (`_female` or `_male`, for a language
 * whose first person is gendered), then the one form every voice shares.
 * English is the bare key; every other language sits under it by tag, as
 * `spokenKey` does. Filler 1 has its two forms in hi, hi-Latn, mr, gu and pa;
 * everywhere else the first candidate finds nothing and the shared form is
 * said, and adding a form changes no code.
 */
export const clipKeys = (kind: AgentClipKind, n: number, tag: AgentLanguageTag, voice: AgentVoice): readonly string[] => {
  const stem = kind === 'filler' ? `agent.filler.${n}` : 'account.voice_sample';
  const base = tag === 'en' ? stem : `${stem}.${tag}`;
  return [`${base}_${voice}`, base];
};

/* ------------------------------------------------ The app language */

/**
 * The language the app is in, one setting per person (`auth.user.app_language`),
 * and the language OS speaks (contract section 13: "OS speaks the app
 * language"). Picked on the web before setup, from the languages for where the
 * person seems to be.
 *
 * A wider list than the agent's eleven: the India list adds Odia, and the USA
 * list adds six languages OS does not speak yet. `agentLanguageForApp` is the
 * one bridge between the two lists, and it falls back to English for a
 * language OS does not speak. Hinglish stays an app language so a phone's
 * stored choice is still one; it is in no region's list, only in "all".
 *
 * The phone keeps reading its own choice (`apps/mobile/src/lib/app-language.ts`,
 * the eleven): this list changes nothing there.
 */
export const APP_LANGUAGE_TAGS = [
  'en', 'hi', 'hi-Latn', 'bn', 'ta', 'te', 'mr', 'gu', 'kn', 'ml', 'pa', 'or',
  'es', 'zh', 'vi', 'tl', 'ko', 'fr',
] as const;

export type AppLanguageTag = (typeof APP_LANGUAGE_TAGS)[number];

export type AppLanguage = {
  readonly tag: AppLanguageTag;
  /** The name in the language itself, in its own script: the row's large line. */
  readonly native: string;
  /** The name in English, beside it; also what the search matches. */
  readonly english: string;
};

/** A language OS speaks takes its names from `AGENT_LANGUAGES`, so the two lists cannot name it twice. */
const spoken = (tag: AgentLanguageTag): AppLanguage => {
  const { native, english } = agentLanguageOf(tag);
  return { tag, native, english };
};

export const APP_LANGUAGES: readonly AppLanguage[] = [
  spoken('en'),
  spoken('hi'),
  spoken('hi-Latn'),
  spoken('bn'),
  spoken('ta'),
  spoken('te'),
  spoken('mr'),
  spoken('gu'),
  spoken('kn'),
  spoken('ml'),
  spoken('pa'),
  { tag: 'or', native: 'ଓଡ଼ିଆ', english: 'Odia' },
  { tag: 'es', native: 'Español', english: 'Spanish' },
  { tag: 'zh', native: '中文', english: 'Chinese' },
  { tag: 'vi', native: 'Tiếng Việt', english: 'Vietnamese' },
  { tag: 'tl', native: 'Tagalog', english: 'Tagalog' },
  { tag: 'ko', native: '한국어', english: 'Korean' },
  { tag: 'fr', native: 'Français', english: 'French' },
];

export const DEFAULT_APP_LANGUAGE: AppLanguageTag = 'en';

export const isAppLanguageTag = (value: unknown): value is AppLanguageTag =>
  typeof value === 'string' && (APP_LANGUAGE_TAGS as readonly string[]).includes(value);

export const appLanguageOf = (tag: AppLanguageTag): AppLanguage =>
  APP_LANGUAGES.find((language) => language.tag === tag) ?? APP_LANGUAGES[0]!;

/**
 * The language OS speaks for an app language: the same language when OS
 * speaks it, English otherwise. Nothing chosen, or a value this build does not
 * know (a later build's tag, a hand-edited row), is English too.
 */
export const agentLanguageForApp = (tag: string | null | undefined): AgentLanguageTag =>
  isAgentLanguageTag(tag) ? tag : DEFAULT_AGENT_LANGUAGE;

/**
 * Scripts none of the eleven agent languages are written in, each with the
 * language it most likely is (step 9.12): a line mostly in one is in a
 * language OS does not speak. Han is read as Chinese, the US list's language.
 */
const UNSPOKEN_SCRIPTS: readonly { readonly script: RegExp; readonly language: string }[] = [
  { script: /[\u0B00-\u0B7F]/u, language: 'Odia' },
  { script: /[\u0D80-\u0DFF]/u, language: 'Sinhala' },
  { script: /[\u0E00-\u0E7F]/u, language: 'Thai' },
  { script: /[\u4E00-\u9FFF]/u, language: 'Chinese' },
  { script: /[\uAC00-\uD7AF]/u, language: 'Korean' },
];

/** A letter or a combining mark: what a script's share is counted over (Odia's vowel signs are marks). */
const LETTER = /[\p{L}\p{M}]/u;

/**
 * Whether OS must ask which language to talk in before it answers (9.12):
 * the app language is one OS does not speak (Odia, and six of the US list),
 * or the line is mostly (over half its letters) in a script none of the
 * eleven use. Answers that language's English name; null when OS speaks it,
 * so it never answers in English silently.
 */
export const languageAskOf = ({ appLanguage, text }: { readonly appLanguage: string | null | undefined; readonly text: string | null | undefined }): { readonly language: string } | null => {
  if (isAppLanguageTag(appLanguage) && !isAgentLanguageTag(appLanguage)) return { language: appLanguageOf(appLanguage).english };
  const letters = [...(text ?? '')].filter((one) => LETTER.test(one));
  if (letters.length === 0) return null;
  for (const { script, language } of UNSPOKEN_SCRIPTS) {
    if (letters.filter((one) => script.test(one)).length * 2 > letters.length) return { language };
  }
  return null;
};

/** The regions with a list of their own (contract section 13). Anywhere else opens on every language. */
export const APP_LANGUAGE_REGIONS = {
  IN: ['en', 'hi', 'bn', 'ta', 'te', 'mr', 'gu', 'kn', 'ml', 'pa', 'or'],
  US: ['en', 'es', 'zh', 'vi', 'tl', 'ko', 'fr'],
} as const satisfies Record<string, readonly AppLanguageTag[]>;

export type AppLanguageRegion = keyof typeof APP_LANGUAGE_REGIONS;

/** The header the web's Worker puts the connection's country in (`request.cf.country`), and the server reads. */
export const CONNECTION_COUNTRY_HEADER = 'x-connection-country' as const;

/** `GET /v1/locale-guess`: the country the connection comes from, or null when nobody said. */
export type LocaleGuessResponse = { country: string | null };

/**
 * An ISO 3166-1 alpha-2 country, upper case, or null. Cloudflare says `XX` for
 * no country and `T1` for Tor; neither is a place, so both are null.
 */
export const readCountry = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code) || code === 'XX' || code === 'T1') return null;
  return code;
};

/** The region subtag of a BCP 47 locale (`en-IN` is IN, `zh-Hans-CN` is CN), or null. */
const regionOfLocale = (locale: string): string | null => {
  for (const part of locale.split(/[-_]/).slice(1)) {
    const country = /^[A-Za-z]{2}$/.test(part) ? readCountry(part) : null;
    if (country !== null) return country;
  }
  return null;
};

/**
 * Where the person seems to be: the connection's country first (the Worker's
 * guess), then the region of the first browser locale that names one. A guess,
 * labelled as one on the page; nothing asks for permission.
 */
export const guessCountry = (connection: string | null | undefined, locales: readonly string[] = []): string | null => {
  const fromConnection = readCountry(connection);
  if (fromConnection !== null) return fromConnection;
  for (const locale of locales) {
    const region = regionOfLocale(locale);
    if (region !== null) return region;
  }
  return null;
};

/** The region with its own list for a country, or null (show every language). */
export const appLanguageRegionOf = (country: string | null | undefined): AppLanguageRegion | null => {
  const code = readCountry(country);
  return code !== null && code in APP_LANGUAGE_REGIONS ? (code as AppLanguageRegion) : null;
};

/** The languages the picker opens on: the region's list in its order, or every language. */
export const appLanguagesFor = (region: AppLanguageRegion | null): readonly AppLanguage[] =>
  region === null ? APP_LANGUAGES : APP_LANGUAGE_REGIONS[region].map((tag) => appLanguageOf(tag));
